import { useQuery } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import {
  ContentfulSupportArticle,
  ContentfulSupportCategory,
  ContentfulSupportLandingPage,
} from '@jbhi-fi/apis/src/contentful/types';

export interface CategoryPageData {
  title: string;
  subCategories: Array<ContentfulSupportCategory>;
  nestedSubCategories: Array<ContentfulSupportCategory>;
  articles: Array<ContentfulSupportArticle>;
  urlSlug: string;
}

export interface UseContentfulHelpAndSupport {
  isLoading: boolean;
  landingPage?: ContentfulSupportLandingPage;
  categoryPage?: CategoryPageData;
  articlePage?: ContentfulSupportArticle;
}

export const useContentfulHelpAndSupport = ({
  handle,
  isPreview,
}: {
  handle: string;
  isPreview?: boolean;
}): UseContentfulHelpAndSupport => {
  const { contentfulThemeApiClient } = usePublicApiClient();
  const refetchInterval = isPreview ? 2500 : false;

  if (!contentfulThemeApiClient) {
    throw new Error('useContentfulHelpAndSupport must be called within a PublicApiClient provider');
  }
  const isLandingPage = handle === '';
  const isSearchResultsPage = handle === 'search';
  const isCategoryOrArticlePage = !isLandingPage && !isSearchResultsPage;

  // Landing page
  const { isLoading: isLandingLoading, data: landingPage } = useQuery({
    enabled: isLandingPage,
    queryKey: ['useContentfulHelpAndSupport', 'landing'],
    refetchOnWindowFocus: false,
    refetchInterval,
    queryFn: () =>
      contentfulThemeApiClient.getEntries<ContentfulSupportLandingPage>(
        {
          content_type: 'supportLandingPage',
          include: '4',
        },
        { shouldRemoveUnresolved: true }
      ),
    select: response => response?.data?.items[0],
  });

  const handleFirstPart = handle.split('-')[0].toLowerCase();
  const legacyId = isNaN(Number(handleFirstPart)) ? undefined : handleFirstPart;

  // Category page
  const { isLoading: isCategoryLoading, data: categoryPage } = useQuery({
    enabled: isCategoryOrArticlePage,
    queryKey: ['useContentfulHelpAndSupport', 'category', handle],
    refetchOnWindowFocus: false,
    refetchInterval,
    queryFn: async () => {
      const filters = {
        content_type: 'supportCategory',
        select: 'sys.id,fields.heading,fields.urlSlug,fields.parentCategory',
        include: '5',
      };
      const categorySubCategoryResponse = await contentfulThemeApiClient.getEntries<ContentfulSupportCategory>({
        ...filters,
        'fields.parentCategory.sys.contentType.sys.id': 'supportCategory',
        ...(legacyId
          ? { 'fields.parentCategory.fields.legacyId': legacyId }
          : { 'fields.parentCategory.fields.urlSlug': handle }),
      });

      let subCategories = categorySubCategoryResponse?.data?.items ?? [];
      let categoryPageTitle = subCategories?.[0]?.fields?.parentCategory?.fields?.heading || '';
      let categoryPageUrlSlug = subCategories?.[0]?.fields?.parentCategory?.fields?.urlSlug || '';

      if (!subCategories.length) {
        const categoryResponse = await contentfulThemeApiClient.getEntries<ContentfulSupportCategory>({
          ...filters,
          ...(legacyId ? { 'fields.legacyId': legacyId } : { 'fields.urlSlug': handle }),
        });
        subCategories = categoryResponse?.data?.items ?? [];
        categoryPageTitle = subCategories?.[0]?.fields?.heading || '';
        categoryPageUrlSlug = subCategories?.[0]?.fields?.urlSlug || '';
      }
      if (!subCategories.length) return;
      const subCategoryIds = subCategories.map(subCategory => subCategory.sys.id)?.join(',');

      let nestedSubCategories: Array<ContentfulSupportCategory> = [];
      const nestedSubCategoriesResponse = await contentfulThemeApiClient.getEntries<ContentfulSupportCategory>({
        content_type: 'supportCategory',
        'fields.parentCategory.sys.id[in]': subCategoryIds,
        select: 'sys.id,fields.heading,fields.urlSlug,fields.parentCategory',
        include: '0',
      });
      nestedSubCategories = nestedSubCategoriesResponse?.data?.items ?? [];

      let articles: Array<ContentfulSupportArticle> = [];
      const categoryArticlesResponse = await contentfulThemeApiClient.getEntries<ContentfulSupportArticle>({
        content_type: 'supportArticle',
        'fields.supportCategory.sys.id[in]': subCategoryIds,
        select: 'fields.heading,fields.urlSlug,fields.supportCategory',
        include: '0',
      });
      articles = categoryArticlesResponse?.data?.items ?? [];

      return {
        urlSlug: categoryPageUrlSlug,
        subCategories,
        nestedSubCategories,
        articles,
        title: categoryPageTitle,
      };
    },
  });

  // Article page
  const { isLoading: isArticleLoading, data: articlePage } = useQuery({
    enabled: isCategoryOrArticlePage,
    queryKey: ['useContentfulHelpAndSupport', 'article', handle],
    refetchOnWindowFocus: false,
    refetchInterval,
    queryFn: () =>
      contentfulThemeApiClient.getEntries<ContentfulSupportArticle>({
        content_type: 'supportArticle',
        ...(legacyId ? { 'fields.legacyId': legacyId } : { 'fields.urlSlug': handle }),
        include: '6',
      }),
    select: response => response?.data?.items[0],
  });

  return {
    isLoading: isLandingLoading || isCategoryLoading || isArticleLoading,
    landingPage,
    categoryPage,
    articlePage,
  };
};
